import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { POSTS_BY_DATE, CATEGORIES, formatDate } from '../data/blogPosts';
import Seo from '../components/Seo';

const CategoryPill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-1.5 text-sm font-medium transition-all duration-200 border ${
      active
        ? 'bg-black text-white border-black'
        : 'bg-white text-gray-600 border-gray-300 hover:border-black hover:text-black'
    }`}
  >
    {label}
  </button>
);

const PostCard = ({ post }) => (
  <Link to={`/blog/${post.id}`} className="text-left group block">
    <article className="bg-white border border-gray-200 hover:border-black transition-all duration-300 hover:shadow-lg h-full">
      {post.image && (
        <div className="aspect-video overflow-hidden bg-gray-100">
          <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
            <span className="text-gray-400 text-xs uppercase tracking-widest">
              Featured Image
            </span>
          </div>
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            {post.category}
          </span>
          <span className="text-gray-300">|</span>
          <span className="text-xs text-gray-400">{`${post.readTime} min read`}</span>
        </div>
        <h3 className="text-xl font-black text-black group-hover:underline decoration-2 underline-offset-4 leading-tight mb-2">
          {post.title}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">{formatDate(post.date)}</span>
          <span className="text-xs font-bold uppercase tracking-wider text-black opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Read →
          </span>
        </div>
      </div>
    </article>
  </Link>
);

const FeaturedPost = ({ post }) => (
  <Link to={`/blog/${post.id}`} className="text-left group block">
    <article className="bg-black text-white p-8 md:p-12 hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 -translate-y-1/2 translate-x-1/2" />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-white text-black px-3 py-1 text-xs font-bold uppercase tracking-widest">
            Featured
          </span>
          <span className="text-xs text-gray-400 uppercase tracking-widest">
            {post.category}
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-black leading-tight mb-4 group-hover:underline decoration-2 underline-offset-8">
          {post.title}
        </h2>
        <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mb-6">
          {post.excerpt}
        </p>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-400">{formatDate(post.date)}</span>
          <span className="text-gray-600">·</span>
          <span className="text-sm text-gray-400">{`${post.readTime} min read`}</span>
        </div>
      </div>
    </article>
  </Link>
);

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered =
    activeCategory === 'All'
      ? POSTS_BY_DATE
      : POSTS_BY_DATE.filter((p) => p.category === activeCategory);

  const featured = filtered.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-white text-black">
    <Seo title="Blog — Integration, EDI & AI in Logistics" description="Practitioner notes on B2B integration architecture, EDI modernization, Amazon Vendor Central compliance, and AI in logistics." path="/blog" />
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section header */}
          <div className="mb-12">
            <h1 className="text-5xl md:text-6xl font-black mb-4 text-black">Blog</h1>
            <p className="text-gray-500 text-lg max-w-xl">
              Thinking out loud on integration architecture, AI in logistics,
              and building technology that actually ships.
            </p>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map((cat) => (
              <CategoryPill
                key={cat}
                label={cat}
                active={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
              />
            ))}
          </div>

          {/* Featured post */}
          {featured && (
            <div className="mb-12">
              <FeaturedPost post={featured} />
            </div>
          )}

          {/* Post grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg">
                No posts in this category yet.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
